/**
 * Saved search observability service for tracking saved search events.
 *
 * Tracks saved search create, delete, and update failures. Tenant ID is auto-injected by the event-tracker.
 */

import { trackEvent } from '../utils/event-tracker';
import {
  SAVED_SEARCH_CREATE_FAILURE,
  SAVED_SEARCH_DELETE_FAILURE,
  SAVED_SEARCH_UPDATE_FAILURE,
} from '../constants/saved-search-events';

/**
 * Optional context sent with saved search failure events (id and/or filter).
 */
export interface SavedSearchFailureErrorData {
  savedSearchId?: string;
  filterData?: Record<string, unknown>;
}

/**
 * Parameters for tracking any saved search failure (create, delete, or update).
 */
export interface SavedSearchFailureParams {
  /** The error that caused the failure */
  error: unknown;
  /** Optional context (e.g. savedSearchId, filterData) */
  errorData?: SavedSearchFailureErrorData;
}

/**
 * Converts any error value to a string for observability.
 * No shared API error format exists; we stringify so we never lose information.
 */
function errorToString(error: unknown): string {
  if (error == null) {
    return 'Unknown error';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message || String(error);
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export const SavedSearchObservabilityService = {
  trackCreateFailure(params: SavedSearchFailureParams): void {
    const { error, errorData } = params;

    trackEvent(SAVED_SEARCH_CREATE_FAILURE, {
      errorMessage: errorToString(error),
      ...errorData,
    });
  },

  trackDeleteFailure(params: SavedSearchFailureParams): void {
    const { error, errorData } = params;

    trackEvent(SAVED_SEARCH_DELETE_FAILURE, {
      errorMessage: errorToString(error),
      ...errorData,
    });
  },

  trackUpdateFailure(params: SavedSearchFailureParams): void {
    const { error, errorData } = params;

    trackEvent(SAVED_SEARCH_UPDATE_FAILURE, {
      errorMessage: errorToString(error),
      ...errorData,
    });
  },
} as const;
